import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { AlertTriangle, Clock } from "lucide-react";

const STAGES = ["new", "qualified", "discovery", "proposal", "negotiation", "won", "lost", "nurture"];
const STAGE_LABEL = { new: "New Enquiry", qualified: "Qualified", discovery: "Discovery", proposal: "Proposal", negotiation: "Negotiation", won: "Won", lost: "Lost", nurture: "Nurture" };
const STAGE_COLOR = { new: "bg-blue-50 text-blue-700", qualified: "bg-cyan-50 text-cyan-700", discovery: "bg-indigo-50 text-indigo-700", proposal: "bg-amber-50 text-amber-700", negotiation: "bg-orange-50 text-orange-700", won: "bg-green-50 text-green-700", lost: "bg-red-50 text-red-600", nurture: "bg-gray-100 text-gray-600" };

export const PartnerLeadsPipeline = () => {
  const [leads, setLeads] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/partner-enquiries").then((r) => setLeads(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const setStage = async (id, stage) => {
    try {
      await api.put(`/admin/partner-enquiries/${id}`, { stage });
      toast.success(`Moved to ${STAGE_LABEL[stage]}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const cats = [...new Set(leads.map((l) => l.category_label))];
  const shown = leads.filter((l) => catFilter === "all" || l.category_label === catFilter);
  const counts = STAGES.reduce((m, s) => ({ ...m, [s]: leads.filter((l) => l.stage === s).length }), {});
  const overdue = leads.filter((l) => l.overdue).length;

  return (
    <div className="space-y-5" data-testid="partner-leads-pipeline">
      <div className="bg-white rounded-lg border border-[#e2ebe4] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="heading-upper text-sm tracking-widest text-[#144733]">Partner leads pipeline</h2>
            <p className="text-xs text-[#57685e] mt-1">{leads.length} enquiries · CRM sync MOCKED · acknowledgement emails logged, not sent</p>
          </div>
          {overdue > 0 && (
            <span className="bg-red-50 text-red-600 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid="pipeline-overdue-count">
              <AlertTriangle className="w-3.5 h-3.5" /> {overdue} overdue response{overdue > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-4" data-testid="pipeline-stage-counts">
          {STAGES.map((s) => (
            <span key={s} className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${STAGE_COLOR[s]}`}>{STAGE_LABEL[s]} {counts[s]}</span>
          ))}
        </div>
        <select className="field-input mt-4 w-auto" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="pipeline-category-filter">
          <option value="all">All categories</option>
          {cats.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-[#e2ebe4] divide-y divide-[#f1f3f1]" data-testid="pipeline-list">
        {shown.length === 0 && <p className="p-6 text-xs text-[#8a978f] text-center">No partner enquiries yet.</p>}
        {shown.map((l) => (
          <div key={l.enquiry_id} className="p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button className="text-left min-w-0" onClick={() => setOpen(open === l.enquiry_id ? null : l.enquiry_id)} data-testid={`pipeline-row-${l.enquiry_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-[#0F1E17]">{l.company}</span>
                  <span className="text-[10px] text-[#8a978f]">{l.enquiry_id}</span>
                  <span className="bg-[#f0f5f0] text-[#144733] text-[10px] px-2 py-0.5 rounded-full">{l.category_label}</span>
                  {l.overdue && <span className="bg-red-50 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Overdue</span>}
                </div>
                <div className="text-[11px] text-[#57685e] mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{l.name} · {l.email}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Owner: {l.owner} · SLA {l.sla_hours}h</span>
                </div>
              </button>
              <select className="field-input !w-auto !py-1.5 !text-xs" value={l.stage} onChange={(e) => setStage(l.enquiry_id, e.target.value)} data-testid={`pipeline-stage-${l.enquiry_id}`}>
                {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
              </select>
            </div>
            {open === l.enquiry_id && (
              <div className="mt-3 bg-[#fafcfa] border border-dashed border-[#e2ebe4] rounded p-3 text-[11px] text-[#41504a] space-y-1" data-testid={`pipeline-detail-${l.enquiry_id}`}>
                <p><strong>Message:</strong> {l.message}</p>
                <p><strong>Market:</strong> {l.market} · <strong>Volume:</strong> {l.monthly_requirement} · <strong>Phone:</strong> {l.phone}</p>
                {Object.keys(l.qualification || {}).length > 0 && (
                  <p><strong>Qualification:</strong> {Object.entries(l.qualification).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
                )}
                <p className="text-[#8a978f]"><strong>Attribution:</strong> {l.landing_page || "—"} · UTM {JSON.stringify(l.utm || {})} · ref {l.referrer || "—"} · {l.device_type} · form {l.form_time_seconds ?? "—"}s · consent {l.consent_accepted_at?.slice(0, 16)} ({l.policy_version})</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
